class ComparisonChart extends HTMLElement {
  connectedCallback() {
    const sectionId = this.getAttribute('data-section-id');
    this._scrollWrapper = document.getElementById(`scrollWrapper-${sectionId}`);
    this._progressBar = document.getElementById(`progressBar-${sectionId}`);

    // Bind listener references so they can be removed in disconnectedCallback
    this._onDocClick = (e) => {
      if (!e.target.closest('[data-help-trigger]')) this.closeAllTooltips();
    };
    this._onScroll = () => this._updateProgressBar();
    this._onResize = () => this.updateStickyOffsets();

    this._initTooltips();

    // Close tooltips on outside click (document-scoped — tooltips may live outside the component)
    document.addEventListener('click', this._onDocClick);

    if (this._scrollWrapper) {
      this._scrollWrapper.addEventListener('scroll', this._onScroll);

      // On Mobile: Fire GA4 scroll milestone once when the final column becomes visible.
      const lastTh = this.querySelector('thead th:last-child');
      if (lastTh) {
        this._scrollMilestoneObserver = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              this.ga4TrackEvent('comparison_chart_scroll');
              this._scrollMilestoneObserver.disconnect();
            }
          },
          {root: this._scrollWrapper, threshold: 0.5},
        );
        this._scrollWrapper.addEventListener(
          'scroll',
          () => this._scrollMilestoneObserver.observe(lastTh),
          {once: true},
        );
      }
    }

    // Use ResizeObserver for accurate, performant layout recalculation
    this._resizeObserver = new ResizeObserver(() => this.updateStickyOffsets());
    this._resizeObserver.observe(document.documentElement);

    // Run layout calculations on initial load
    this.columnWidth();
    this.updateStickyOffsets();

    // Fire GA4 event once when ≥50% of the chart enters the viewport
    this._viewObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this.ga4TrackEvent('view_comparison_chart');
          this._viewObserver.disconnect();
        }
      },
      {threshold: 0.5},
    );
    this._viewObserver.observe(this);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);

    if (this._scrollWrapper) {
      this._scrollWrapper.removeEventListener('scroll', this._onScroll);
    }

    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }

    if (this._viewObserver) {
      this._viewObserver.disconnect();
    }

    if (this._scrollMilestoneObserver) {
      this._scrollMilestoneObserver.disconnect();
    }
  }

  _initTooltips() {
    // Scope to this element — help triggers are part of this component
    this.querySelectorAll('[data-help-trigger]').forEach((btn) => {
      const tooltip = document.getElementById(
        btn.getAttribute('aria-controls'),
      );
      if (!tooltip) return;

      // Toggle on click (touch/mobile users)
      btn.addEventListener('click', () => {
        const isVisible = tooltip.dataset.visible === 'true';
        this.closeAllTooltips();
        if (!isVisible) {
          tooltip.dataset.visible = 'true';
          tooltip.setAttribute('aria-hidden', 'false');
          btn.setAttribute('aria-expanded', 'true');
        }
      });

      // Dismiss on Escape key
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          tooltip.dataset.visible = 'false';
          tooltip.setAttribute('aria-hidden', 'true');
          btn.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  closeAllTooltips() {
    document.querySelectorAll('.help-tooltip').forEach((t) => {
      t.dataset.visible = 'false';
      t.setAttribute('aria-hidden', 'true');
      const btn = document.querySelector(`[aria-controls='${t.id}']`);
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
  }

  columnWidth() {
    const columnCount = this.querySelectorAll('thead th').length;
    if (columnCount <= 1) return;

    const colWidth = 1020 / (columnCount - 1);
    const w = window.innerWidth;
    let colMaxWidth;

    if (w > 1400) {
      colMaxWidth = 340;
    } else if (w > 1300) {
      colMaxWidth = 300;
    } else if (w > 1025) {
      colMaxWidth = 160;
    } else {
      return;
    }

    document.body.style.setProperty(
      '--chart-desktop',
      `${Math.min(colWidth, colMaxWidth)}px`,
    );
  }

  updateStickyOffsets() {
    const firstCols = document.querySelectorAll('.js-col-1');
    const secondCols = document.querySelectorAll('.js-col-2');

    if (firstCols.length === 0) return;

    // 1. Position the first column at exactly left: 0
    firstCols.forEach((col) => (col.style.left = '0px'));

    // 2. Measure the exact layout width of the first header cell
    const firstColumnWidth = firstCols[0].getBoundingClientRect().width;

    // 3. Push the second column to the right by that exact width
    secondCols.forEach((col) => {
      col.style.left = `${firstColumnWidth}px`;
    });

    this.columnWidth();
  }

  _updateProgressBar() {
    if (!this._scrollWrapper || !this._progressBar) return;

    const maxScroll =
      this._scrollWrapper.scrollWidth - this._scrollWrapper.clientWidth;
    const scrollPercent =
      maxScroll > 0 ? (this._scrollWrapper.scrollLeft / maxScroll) * 100 : 0;

    this._progressBar.style.width = `${scrollPercent}%`;
  }

  ga4TrackEvent(eventName) {
    const ga4Object = {
      event: eventName,
      event_params: {
        cta_location: this.dataset.ga4CtaLocation,
        content_type: 'comparison_chart',
        content_name: this.dataset.ga4ContentName,
      },
    };

    if (eventName === 'comparison_chart_scroll') {
      ga4Object.event_params.scroll_direction = 'horizontal';
      ga4Object.event_params.scroll_milestone = 'final_column';
    }

    ExecuteGA4Event(ga4Object);
  }
}

customElements.define('petivity-comparison-chart', ComparisonChart);
